import { useLazyQuery } from '@apollo/client';
import { useEffect, useState, useCallback, useRef } from 'react';
import { AppState } from 'react-native';
import { GET_UNREAD_NOTIFICATIONS_COUNT } from '@/shared/queries';
import SentryService from '@/services/sentry/Sentry.service';
import { useGetUser } from './useGetUser';
import { useAuth } from '@/services/auth/AuthProvider';

// Poll every 15 seconds when app is active
const UNREAD_NOTIFICATIONS_POLL_INTERVAL_MS = 15000;

// Global singleton pattern to prevent duplicate intervals
let globalIntervalRef: NodeJS.Timeout | null = null;
let globalInstanceCount = 0;
let globalPollingInstanceCount = 0; // Track how many instances need polling
let globalExecuteQuery: (() => void) | null = null;

interface UseUnreadNotificationsResult {
  unreadCount: number;
  hasUnreadNotifications: boolean;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface UseUnreadNotificationsOptions {
  disablePolling?: boolean;
}

export function useUnreadNotifications(
  options: UseUnreadNotificationsOptions = {},
): UseUnreadNotificationsResult {
  const { disablePolling = false } = options;
  const { isAuthenticated } = useAuth();
  const { user } = useGetUser();
  const [appState, setAppState] = useState(AppState.currentState);
  const previousShouldPoll = useRef<boolean>(false);

  const shouldPoll =
    appState === 'active' && !disablePolling && isAuthenticated && !!user;

  // Using lazy query approach to prevent duplicate queries
  const [getUnreadNotificationsCount, { data, loading, error }] = useLazyQuery(
    GET_UNREAD_NOTIFICATIONS_COUNT,
    {
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      onError: (error) => {
        SentryService.captureError(
          'Error fetching unread notifications count',
          error,
        );
      },
    },
  );

  // Stable execute function
  const executeQuery = useCallback(() => {
    if (user && isAuthenticated) {
      getUnreadNotificationsCount();
    }
  }, [getUnreadNotificationsCount, user, isAuthenticated]);

  // Manual refetch function
  const refetch = useCallback(() => {
    if (user) {
      executeQuery();
    }
  }, [executeQuery, user]);

  // Track app state changes for foreground/background detection
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      // Refetch when app comes to foreground, but only if user is authenticated
      if (
        nextAppState === 'active' &&
        appState !== 'active' &&
        isAuthenticated &&
        user
      ) {
        refetch();
      }
      setAppState(nextAppState);
    });

    return () => subscription?.remove();
  }, [appState, refetch, isAuthenticated, user]);

  // Instance management and global polling coordination
  useEffect(() => {
    globalInstanceCount++;

    return () => {
      globalInstanceCount--;

      // If this instance was polling when unmounted, decrement count
      if (previousShouldPoll.current) {
        globalPollingInstanceCount--;
      }

      // Clear global interval if this was the last instance
      if (globalInstanceCount === 0 && globalIntervalRef) {
        clearInterval(globalIntervalRef);
        globalIntervalRef = null;
        globalExecuteQuery = null;
        globalPollingInstanceCount = 0;
      }
    };
  }, []);

  // Global polling logic - only one instance manages the interval
  useEffect(() => {
    const wasGloballyPolling = globalPollingInstanceCount > 0;

    // Update polling count based on this instance's state change
    if (shouldPoll && !previousShouldPoll.current) {
      // Instance started needing polling
      globalPollingInstanceCount++;
    } else if (!shouldPoll && previousShouldPoll.current) {
      // Instance stopped needing polling
      globalPollingInstanceCount--;
    }

    // Update previous state
    previousShouldPoll.current = shouldPoll;

    // Set the global execute function to this instance's function if we should poll
    if (shouldPoll) {
      globalExecuteQuery = executeQuery;

      // Execute immediately if global polling just started
      if (!wasGloballyPolling && globalPollingInstanceCount > 0) {
        executeQuery();
      }

      // Create global interval if none exists and we have instances that need polling
      if (!globalIntervalRef && globalPollingInstanceCount > 0) {
        __DEV__ && console.log('Starting global unread notifications polling');
        globalIntervalRef = setInterval(() => {
          if (globalExecuteQuery && globalPollingInstanceCount > 0) {
            try {
              globalExecuteQuery();
            } catch (e) {
              SentryService.captureError(
                'Error executing global unread notifications query',
                e,
              );
            }
          }
        }, UNREAD_NOTIFICATIONS_POLL_INTERVAL_MS);
      }
    } else {
      // Stop polling if no instances need it
      if (globalPollingInstanceCount === 0 && globalIntervalRef) {
        __DEV__ && console.log('Stopping global unread notifications polling');
        clearInterval(globalIntervalRef);
        globalIntervalRef = null;
        globalExecuteQuery = null;
      }
    }
  }, [shouldPoll, executeQuery]);

  const unreadCount = data?.unreadNotificationsCount || 0;
  const hasUnreadNotifications = unreadCount > 0;

  return {
    unreadCount,
    hasUnreadNotifications,
    loading,
    error: error || null,
    refetch,
  };
}

export default useUnreadNotifications;
